<template lang="pug">
	.bottom-audio-player
		.player.flex-col.center(ref="player", :class="{ visible: visible }")
			.flex-row.justify.header
				.flex-col
					.title {{title}}
					.fileName {{fileName}}
				.flex-row.space-large.controls(@mousedown.stop="")
					.flex-row.space-large.player-actions(v-if="fileData")
						slot(name="actions-left")
						a.justify(@click="togglePlaying", :title="$tCaption('Toggle play')")
							.playing-state
								i.fa(:class="[isPlaying ? 'fa-pause' : 'fa-play']")
						a.justify(v-show="baseActionsVisible", :title="$tCaption('Skip 10 seconds backward')")
							fas(icon="backward", @click="changePlay(-10)")
						a.justify(v-show="baseActionsVisible", :title="$tCaption('Skip 10 seconds forward')")
							fas(icon="forward", @click="changePlay(10)")
						slot(name="actions")
						a.justify(id="volume-control", @click="() => isVolumeControlVisible = !isVolumeControlVisible", :title="$tCaption('Volume')")
							i.fa.fa-volume-up
					.close-button.flex-row.align-center.center.clickable.hoverable(@click="hide", :title="$tCaption('Close', '@actions')")
						.flex-row
							fas(icon="times")
					slot(v-if="fileData", name="actions-right")
			.loader.flex-col.center(v-if="isLoading")
				.line(:style="{width: `${Math.max(1, loadingProgress)}%` }")
			.av-player(v-if="fileData")
				audio(ref='audioSrc', :autoplay="playState", :src="fileData", @ended="v => isPlaying = false", @play="() => setVolume(currentVolume)")
				av-waveform.waveform(
					v-if="isWaveformActive",
					:audio-controls="false",
					:canv-width="canvasWidth",
					:canv-height="canvasHeight",
					noplayed-line-color="#c4c1dc",
					played-line-color="#f27649",
					:playedLineWidth="1.5",
					ref-link="audioSrc",
					ref="waveForm",
					:markers="markers",
					@timeline-click="time => $emit('timeline-click', time)",
					@initiated="(peaks, segSize) => $emit('initiated', peaks, segSize)")
				.duration {{durationFormatted}}
			.selection(v-if="hasSelection")
				.selection-zone(:style="{ '--start': `${this.selectedRangePx[0]}px`, '--width': `${this.selectedRangePx[1] - this.selectedRangePx[0]}px` }")
			DxPopover(
				:width="300"
				:visible.sync="isVolumeControlVisible"
				:shading="true"
				target="#volume-control"
				position="top"
				shading-color="rgba(0, 0, 0, 0)"
			)
				.dx-template-wrapper
					.flex-row.justify.align-center.bottom-audio-player
						a(@click="() => currentVolume = currentVolume > 0 ? 0 : 100")
							i.fa.fa-volume-up
						DxSlider(v-model="currentVolume", :min="0", :max="100", width="100%")
						.volume-value {{currentVolume}}
</template>


<script>
	import axios from 'axios';
	import _ from 'lodash';
	import $ from 'jquery';
	import VueTypes from 'vue-types';
	import moment from 'moment';
	import { DxPopover } from 'devextreme-vue/popover';
	import { DxSlider } from 'devextreme-vue/slider';

	export default {
		translationCategory: 'audioPlayer.BottomAudioPlayer',
		name: 'bottom-audio-player',
		components: {DxPopover, DxSlider},
		props: {
			visible: VueTypes.bool.def(false),
			src: VueTypes.any.def(null),
			playState: VueTypes.bool.def(false),
			fileName: VueTypes.string.def(null),
			title: VueTypes.string.def(null),
			duration: VueTypes.number.def(null),
			markers: VueTypes.array.def([]),
			selectionMode: VueTypes.bool.def(false),
			baseActionsVisible: VueTypes.bool.def(true)
		},
		data() {
			return {
				isPlaying: false,
				canvasWidth: 400,
				canvasHeight: 90,
				isWaveformActive: true,
				fileData: null,
				isLoading: true,
				loadingProgress: null,
				isVolumeControlVisible: false,
				currentVolume: 100,
				blob: null,
				selection: {
					start: null,
					end: null,
					processing: false,
					active: false
				},
				buffer: null
			};
		},
		computed: {
			durationFormatted() {
				return moment.utc(this.duration * 1000).format('mm:ss');
			},
			selectedRange() {
				let vals = [Math.min(this.selection.start, this.selection.end), Math.max(this.selection.start, this.selection.end)].filter(f => f !== null)

				return vals.map(f => Math.floor(f * 100) / 100);
			},
			hasSelection() {
				return this.selectionMode && this.selectedRange.length > 1 && this.selectedRange[1] - this.selectedRange[0] >= 0.02 || this.selection.active;
			},
			selectedRangePx() {
				return this.selectedRange.map(f => f * this.pixelPerSecond);
			},
			pixelPerSecond() {
				return (this.canvasWidth / this.buffer?.duration) || 0;
			}
		},
		watch: {
			isPlaying(v) {
				$(this.$el).find('.playing-state').html(v ? '<i class="fas fa-pause"/>' : '<i class="fas fa-play"/>');
				if (this.$refs.audioSrc) {
					if (v) {
						this.$refs.audioSrc.play();
					} else {
						this.$refs.audioSrc.pause();
					}
				}
				this.$forceUpdate();
				this.$emit('isPlaying', v);
			},
			playState(v) {
				this.isPlaying = v;
			},
			visible(v) {
				this.setWidth();
				if (!v) {
					this.isPlaying = false;
				}
			},
			src() {
				if (__DEBUG__)
					console.info('Source audio changed');
				this.clearSelection();
				this.fileData = null;
				this.loadFile();
			},
			currentVolume(v) {
				this.setVolume(v);
			}
		},
		mounted() {
			this.setWidth();

			this.$setWidthDebounced = _.debounce(() => this.setWidth(), 100);
			$(window).resize(this.$setWidthDebounced);

			document.body.appendChild(this.$el);

			if (__DEBUG__)
				window.bottomAudioPlayer = this;

			if (this.selectionMode) {
				document.addEventListener('mouseup', this.onMouseUp);
				document.addEventListener('mousedown', this.onMouseDown);
				document.addEventListener('mousemove', this.onMouseMove);
			}
		},
		destroyed() {
			$(window).off('resize', this.$setWidthDebounced);
			delete this.$setWidthDebounced;

			document.removeEventListener('mouseup', this.onMouseUp);
			document.removeEventListener('mousedown', this.onMouseDown);

			if (document.body.contains(this.$el)) {
				document.body.removeChild(this.$el);
			}
		},
		methods: {
			clearSelection() {
				this.selection.start = null;
				this.selection.end = null;
				this.selection.active = false;
				this.$emit('selection', this.hasSelection, this.selectedRange);
			},
			isPlayer(elem) {
				if (!elem) return false;

				if (elem === this.$refs.player) return true;

				return this.isPlayer(elem?.parentElement);
			},
			onMouseUp() {
				this.selection.processing = false;
				this.selection.active = this.selectedRange[1] - this.selectedRange[0] >= 0.02;
				if (!this.selection.active) {
					this.clearSelection();
				} else {
					this.$emit('selection', this.hasSelection, this.selectedRange);
				}
			},
			onMouseDown(e) {
				if (this.isPlayer(e.srcElement)) {
					this.selection.start = e.offsetX / this.pixelPerSecond;
					this.selection.end = this.selection.start;
					this.selection.processing = true;
				}
			},
			onMouseMove(e) {
				if (this.selection.processing) {
					this.selection.end = Math.max(0, Math.min(this.buffer?.duration, this.selection.end + e.movementX / this.pixelPerSecond));
				}
			},
			setPosition(v) {
				this.$refs.audioSrc.currentTime = v;
				this.$refs.waveForm.waveform();
			},
			changePlay(deltaSec) {
				this.$refs.audioSrc.currentTime += deltaSec;
				this.$refs.waveForm.waveform();
			},
			setVolume(v) {
				if (this.$refs.audioSrc)
					this.$refs.audioSrc.volume = v / 100;
			},
			hide() {
				this.isPlaying = false;
				this.$emit('hide');
			},
			togglePlaying() {
				this.isPlaying = !this.isPlaying;
			},
			setWidth() {
				if (__DEBUG__)
					console.info('Resize player');
				var newWidth = $(this.$refs.player).width();

				if (this.canvasWidth !== newWidth) {
					this.canvasWidth = newWidth;
					this.isWaveformActive = false;
					setImmediate(() => {
						if (this._isDestroyed) return;
						this.isWaveformActive = true;
					});
				}
			},
			loadFile() {
				if (!this.src) return;

				let processBlob = async (blob) => {
					this.blob = blob;

					this.buffer = await this.$Data.getAudioBufferFromBlob(blob);
					this.$emit('buffer', this.buffer);

					var fr = new FileReader;
					fr.readAsDataURL(blob);
					fr.onloadend = () => {
						if (this._isDestroyed) return;
						this.fileData = fr.result;
						this.isPlaying = this.playState;
						this.isLoading = false;
					};
				}

				if (this.src instanceof Blob) {
					processBlob(this.src);
					return;
				}
				if (__DEBUG__)
					console.log('Load file', { srcLink: this.src });
				const conf = {
					responseType: 'blob',
					onDownloadProgress: this.downloadProgress
				};
				this.isLoading = true;
				this.loadingProgress = 0;
				axios.get(this.src, conf)
				.then(response => {
					if (this._isDestroyed) return;
					if (__DEBUG__)
						console.info('Get audio data response', { response });

					processBlob(response.data);
				})
				.catch(err => {
					console.error(`Failed to get file '${this.src}'`);
					console.log(err);
				});
			},
			downloadProgress: function (ev) {
				this.loadingProgress = parseFloat(100 * parseFloat(ev.loaded) / parseFloat(ev.total)).toFixed(1);
				if (__DEBUG__)
					console.log('Loading file progress', this.loadingProgress);
			}
		}
	};
</script>
<style lang='scss'>
	@import "main/themes/generated/variables.base.scss";

	.bottom-audio-player {
		> .player {
			box-shadow: 0px 0px 6px rgba(0,0,0,1);
			position: fixed;
			transition: 0.5s;
			align-items: center;
			height: 140px;
			max-height: 50%;
			width: 100%;
			background: #2a2a33;
			transform: translateY(100%);
			z-index: 1501;
			right: 0px;
			user-select: none;
			bottom: 0px;

			&.visible {
				transform: translateY(0%);
			}

			> .header {
				padding: 8px 12px;
				color: #ffffff78;
				position: absolute;
				top: 4px;
				z-index: 1;

				.fileName {
					font-size: 12px;
					color: #ffffff55;
				}

				.title {
					font-size: 18px;
					font-weight: 100;
					color: $base-accent;
				}
			}

			.av-player {
				position: relative;
				width: 100%;
				margin-top: 20px;

				.duration {
					position: absolute;
					bottom: 20px;
					right: 20px;
					color: #ffffff42;
					font-size: 12px;
				}
			}

			.waveform {
				margin-right: 5px;
				width: 100%;
				height: auto;

				canvas {
					width: 100%;
				}
			}

			.loader {
				width: 100%;
				height: 300px;

				.line {
					width: 0%;
					height: 2px;
					background: $base-accent;
				}
			}

			.close-button {
				position: absolute;
				padding: 4px;
				background: rgba(255,255,255,0.2);
				right: -22px;
				top: -4px;
				width: 26px;
				height: 26px;
				border-radius: 50%;
			}

			.controls {
				position: relative;
				margin-right: 18px;
			}

			.selection {
				position: absolute;
				left: 0px;
				top: 0px;
				bottom: 0px;
				right: 0px;
				pointer-events: none;

				> .selection-zone {
					position: absolute;
					height: 100%;
					left: var(--start);
					width: var(--width);
					background: repeating-linear-gradient(
						45deg,
						#ffffff11,
						#ffffff11 20px,
						#cccccc33 20px,
						#cccccc33 40px
					);;
					border-inline: 1px solid #ffffff33;
					writing-mode: horizontal-tb;
				}
			}
		}

		.volume-value {
			font-size: 18px;
			width: 25px;
		}

		.player-actions > * {
			animation: entrance 0.5s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
		}

		@keyframes entrance {
			0% {
				transform: rotateX(-80deg);
				opacity: 0;
			}
			100% {
				transform: rotateX(0);
				opacity: 1;
			}
		}

	}
</style>
